import React, { useState, useEffect } from 'react';
import { ICreateRole, IUpdateRole, IPermissions } from '../../types/roles.types';
import { RolesService } from '../../services/roles.service';
import { toast } from 'react-hot-toast';
import { useAppSelector } from '../../hooks/useRedux';
import Modal from '../common/Modal';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';

interface RoleModalProps {
    isOpen: boolean;
    onClose: () => void;
    role?: IUpdateRole | null;
    onRoleChange: () => void;
}

const initialPermissions: IPermissions = {
    team: {
        view_teams: false,
        create_teams: false,
        update_teams: false,
        delete_teams: false,
        invite_members: false,
        remove_members: false,
        view_team_members: false
    },
    role_management: {
        view_roles: false,
        create_roles: false,
        edit_roles: false,
        delete_roles: false,
        assign_roles: false
    },
    vault: {
        vault_share_teams: false,
        export_items: false
    }
};

type PermissionCategory = keyof typeof initialPermissions;

const permissionCategories: Record<PermissionCategory, string> = {
    team: 'Team Management',
    role_management: 'Role Management',
    vault: 'Vault Access'
};

const RoleModal: React.FC<RoleModalProps> = ({ isOpen, onClose, role, onRoleChange }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [permissions, setPermissions] = useState<IPermissions>(initialPermissions);
    const [activeCategory, setActiveCategory] = useState<PermissionCategory>('team');
    const { team_id } = useAppSelector((state) => state.auth);

    useEffect(() => {
        if (role) {
            setName(role.name);
            setDescription(role.description || '');
            // Merge role permissions with initial permissions structure
            setPermissions({
                team: {
                    ...initialPermissions.team,
                    ...role.permissions?.team
                },
                role_management: {
                    ...initialPermissions.role_management,
                    ...role.permissions?.role_management
                },
                vault: {
                    ...initialPermissions.vault,
                    ...role.permissions?.vault
                }
            });
        } else {
            setName('');
            setDescription('');
            setPermissions(initialPermissions);
        }
    }, [role]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (role) {
                await RolesService.updateRole({ id: role.id, name, description, permissions, team_id: team_id || '' });
                toast.success('Role updated successfully');
            } else {
                await RolesService.createRole({ name, description, permissions, team_id: team_id || '' });
                toast.success('Role created successfully');
            }
            onRoleChange();
            onClose();
        } catch (error) {
            toast.error('Failed to save role');
        }
    };

    const handlePermissionChange = (category: PermissionCategory, permission: string, checked: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [permission]: checked
            }
        }));
    };

    const handleSelectAll = (category: PermissionCategory) => {
        setPermissions(prev => ({
            ...prev,
            [category]: Object.keys(prev[category]).reduce((acc, key) => ({
                ...acc,
                [key]: true
            }), {})
        }));
    };

    const handleDeselectAll = (category: PermissionCategory) => {
        setPermissions(prev => ({
            ...prev,
            [category]: Object.keys(prev[category]).reduce((acc, key) => ({
                ...acc,
                [key]: false
            }), {})
        }));
    };

    const formatPermissionName = (name: string) => {
        return name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const renderPermissionPairs = (category: PermissionCategory) => {
        const permissionEntries = Object.entries(permissions[category]);
        const pairs = [];
        
        for (let i = 0; i < permissionEntries.length; i += 2) {
            const [key1, value1] = permissionEntries[i];
            const [key2, value2] = permissionEntries[i + 1] || [null, null];
            
            pairs.push(
                <div key={key1} className="flex space-x-6 mb-4">
                    <div className="flex items-center flex-1">
                        <div className="relative">
                            <input
                                type="checkbox"
                                id={`${category}-${key1}`}
                                checked={value1 as boolean}
                                onChange={(e) => handlePermissionChange(category, key1, e.target.checked)}
                                className="h-5 w-5 text-primary-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                            />
                        </div>
                        <label
                            htmlFor={`${category}-${key1}`}
                            className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900 transition-colors duration-200"
                        >
                            {formatPermissionName(key1)}
                        </label>
                    </div>
                    {key2 && (
                        <div className="flex items-center flex-1">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    id={`${category}-${key2}`}
                                    checked={value2 as boolean}
                                    onChange={(e) => handlePermissionChange(category, key2, e.target.checked)}
                                    className="h-5 w-5 text-primary-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                                />
                            </div>
                            <label
                                htmlFor={`${category}-${key2}`}
                                className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900 transition-colors duration-200"
                            >
                                {formatPermissionName(key2)}
                            </label>
                        </div>
                    )}
                </div>
            );
        }
        
        return pairs;
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={role ? 'Edit Role' : 'Create Role'}
            headerIcon={<ShieldCheckIcon className="h-6 w-6 text-white" />}
            headerDescription="Define permissions and access levels for team members"
            size="lg"
        >
            <div className="bg-white rounded-2xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Role Basic Info */}
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                                Role Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                                placeholder="Enter role name"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-semibold text-gray-900 mb-2">
                                Description
                            </label>
                            <textarea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                                placeholder="Describe this role and its responsibilities"
                            />
                        </div>
                    </div>

                    {/* Permissions Section */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-4">
                            Permissions
                        </label>
                        <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                            {/* Tab Navigation */}
                            <div className="flex bg-gray-50">
                                {(Object.entries(permissionCategories) as [PermissionCategory, string][]).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`flex-1 px-6 py-4 text-sm font-semibold transition-all duration-200 ${
                                            activeCategory === key
                                                ? 'bg-white text-primary-600 border-b-2 border-primary-500 shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        }`}
                                        onClick={() => setActiveCategory(key)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            
                            {/* Permission Content */}
                            <div className="p-6 bg-white">
                                <div className="flex justify-end space-x-4 mb-6">
                                    <button
                                        type="button"
                                        onClick={() => handleSelectAll(activeCategory)}
                                        className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDeselectAll(activeCategory)}
                                        className="text-sm font-medium text-gray-600 hover:text-gray-700 transition-colors duration-200"
                                    >
                                        Deselect All
                                    </button>
                                </div>
                                <div className="max-h-80 overflow-y-auto">
                                    {renderPermissionPairs(activeCategory)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                        <Button
                            type="button"
                            onClick={onClose}
                            variant="secondary"
                            className="px-6 py-3"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="px-6 py-3"
                        >
                            {role ? 'Update Role' : 'Create Role'}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default RoleModal; 